import { type ComponentProps, useMemo } from 'react';
import type { Transition } from 'framer-motion';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import type { ReferralSource } from '@soundxyz/utils/constants/embeds';
import type { FragmentType } from '@graphql/reactQuery';
import { DynamicFeePercentInfoFragmentDoc, getFragment } from '@graphql/reactQuery';
import type { ShareModalPlaylist, ShareModalRelease } from '.';

/*#__PURE__*/ gql(/* GraphQL */ `
  query releaseShareInfo($id: UUID!) {
    release(id: $id) {
      id
      ...ReleaseShareInfo
    }
  }

  fragment ReleaseShareInfo on Release {
    id
    webappUri
    publicAffiliateFeePercent
    isFirstPhaseCompleted
    samConfig {
      id
      affiliateFeePercent
    }
    ...ReleaseReferrableInfo
    ...DynamicFeePercentInfo
  }

  fragment PlaylistShareInfo on Shelf {
    id
    webappUri
    user {
      id
    }
  }
`);

export type ShareModalProps = (
  | ComponentProps<typeof ShareModalRelease>
  | ComponentProps<typeof ShareModalPlaylist>
) & {
  /** Where in the UI this component is triggered from? This is helpful for analytics purposes */
  source: ShareModalSource;
};

export const sourceLink: ReferralSource = {
  link: true,
};

export const ShareTab = {
  social: { key: 'social', position: 0 } as const,
  image: { key: 'image', position: 1 } as const,
  embed: { key: 'embed', position: 2 } as const,
};

export type ShareTabKey = keyof typeof ShareTab;

export const SHARE_TRANSITION: Transition = {
  duration: 0.25,
  ease: 'easeInOut',
};

export const SHARE_AND_EARN_COPY = (fee: Percent) => `Share & Earn ${fee}` as const;

export const EARN_RELEASE_FEE_COPY = 'Earn curator rewards for each mint driven through your link';

export type Percent = `${number}%`;

export const zeroPercent = '0%';

export const samFeePercent = '2.5%';
export const defaultFeePercent = '5%';

export const defaultFeePercentBPS = 500;

export function isValidPercent(str: string): str is Percent {
  return /^[0-9]+(\.[0-9]+)?%$/.test(str);
}

gql(/* GraphQL */ `
  fragment DynamicFeePercentInfo on Release {
    publicAffiliateFeePercent
    isFirstPhaseCompleted
    samConfig {
      id
      affiliateFeePercent
    }
  }
`);

export function useDynamicFeePercent({
  releaseInfo,
}: {
  releaseInfo: FragmentType<DynamicFeePercentInfoFragmentDoc>;
}) {
  const { publicAffiliateFeePercent, isFirstPhaseCompleted, samConfig } = getFragment(
    DynamicFeePercentInfoFragmentDoc,
    releaseInfo,
  );

  const samConfigAffiliateFeePercent = samConfig?.affiliateFeePercent;

  return useMemo(() => {
    if (isFirstPhaseCompleted && samConfigAffiliateFeePercent != null)
      return isValidPercent(samConfigAffiliateFeePercent)
        ? samConfigAffiliateFeePercent
        : zeroPercent;

    return publicAffiliateFeePercent && isValidPercent(publicAffiliateFeePercent)
      ? publicAffiliateFeePercent
      : zeroPercent;
  }, [isFirstPhaseCompleted, publicAffiliateFeePercent, samConfigAffiliateFeePercent]);
}

export const TWITTER_POST_SHARE_TEXT = (
  postWebappUri: string,
  artistTwitterHandle: Maybe<string>,
) => {
  if (!!artistTwitterHandle) {
    return `👀🎧 Check out this post on @soundxyz_ @${artistTwitterHandle} ${postWebappUri}` as const;
  }

  return `👀🎧 Check out this post on @soundxyz_ ${postWebappUri}` as const;
};

export const REGULAR_POST_NO_URL_SHARE_TEXT = `👀🎧 Check out this post on Sound.xyz`;
export const REGULAR_DROP_NO_URL_SHARE_TEXT = `👀🎧 Check out this drop on Sound.xyz`;
export const REGULAR_PLAYLIST_NO_URL_SHARE_TEXT = `👀🎧 Check out this playlist on Sound.xyz`;

export const REGULAR_POST_SHARE_TEXT = (postWebappUri: string) =>
  `${REGULAR_POST_NO_URL_SHARE_TEXT} ${postWebappUri}` as const;

export function TwitterPostShareLink({
  webappUri,
  artistTwitterHandle,
}: {
  webappUri: string;
  artistTwitterHandle: Maybe<string>;
}) {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    TWITTER_POST_SHARE_TEXT(webappUri, artistTwitterHandle),
  )}` as const;
}

export const AnimationVariants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    };
  },
};
